import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormSchemas } from '../hooks/useFormSchemas';
import { FormSection } from '../types/formSchema';
import FormKindPicker from '../components/admin/FormKindPicker';
import FieldListEditor from '../components/admin/FieldListEditor';
import FieldDetailPanel from '../components/admin/FieldDetailPanel';
import FormPreview from '../components/admin/FormPreview';

export default function AdminFormSchemaPage() {
  const navigate = useNavigate();
  const { schemas, loading, error, saving, saveSchema } = useFormSchemas();

  const [selectedKind, setSelectedKind] = useState('equipment');
  const [localSections, setLocalSections] = useState<FormSection[]>([]);
  const [selectedFieldKey, setSelectedFieldKey] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: 'ok' | 'err' } | null>(null);

  const activeSchema = useMemo(
    () => schemas.find((s) => s.formKind === selectedKind) ?? null,
    [schemas, selectedKind],
  );

  useEffect(() => {
    if (activeSchema) {
      setLocalSections(JSON.parse(JSON.stringify(activeSchema.sections)));
      setDirty(false);
      setSelectedFieldKey(null);
    }
  }, [activeSchema]);

  const handleSectionsChange = useCallback((sections: FormSection[]) => {
    setLocalSections(sections);
    setDirty(true);
  }, []);

  const handleSave = async () => {
    if (!selectedKind) return;
    try {
      await saveSchema(selectedKind, { formKind: selectedKind, sections: localSections });
      setDirty(false);
      setToast({ msg: 'Saved successfully', type: 'ok' });
    } catch (e: any) {
      setToast({ msg: e?.message || 'Save failed', type: 'err' });
    }
    setTimeout(() => setToast(null), 3000);
  };

  const handleKindChange = (kind: string) => {
    if (dirty && !window.confirm('You have unsaved changes. Discard?')) return;
    setSelectedKind(kind);
  };

  return (
    <div style={pageStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => navigate('/')} style={backBtnStyle} title="Back to app">
            &#8592;
          </button>
          <h1 style={titleStyle}>Admin: Form Configuration</h1>
          {dirty && <span style={dirtyBadge}>Unsaved changes</span>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {toast && (
            <span style={{ fontSize: 12, color: toast.type === 'ok' ? '#16a34a' : '#ef4444' }}>
              {toast.msg}
            </span>
          )}
          <button onClick={handleSave} disabled={!dirty || saving} style={saveBtnStyle}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div style={errorBanner}>{error}</div>
      )}

      {/* Loading */}
      {loading && !schemas.length && (
        <div style={loadingStyle}>Loading form schemas...</div>
      )}

      {/* Body */}
      {schemas.length > 0 && (
        <div style={bodyStyle}>
          {/* Left: Form kind picker */}
          <FormKindPicker selected={selectedKind} onChange={handleKindChange} />

          {/* Middle: field list + preview */}
          <div style={middleColStyle}>
            <div style={fieldListWrapStyle}>
              <FieldListEditor
                sections={localSections}
                selectedFieldKey={selectedFieldKey}
                onSelectField={setSelectedFieldKey}
                onSectionsChange={handleSectionsChange}
              />
            </div>
            <FormPreview sections={localSections} />
          </div>

          {/* Right: field detail */}
          <div style={rightColStyle}>
            <FieldDetailPanel
              sections={localSections}
              selectedFieldKey={selectedFieldKey}
              onSectionsChange={handleSectionsChange}
            />
          </div>
        </div>
      )}
    </div>
  );
}

const pageStyle: React.CSSProperties = {
  minHeight: '100%',
  backgroundColor: '#f9fafb',
  fontFamily: 'system-ui, -apple-system, sans-serif',
  display: 'flex',
  flexDirection: 'column',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 24px',
  backgroundColor: '#fff',
  borderBottom: '1px solid #e5e7eb',
  position: 'sticky',
  top: 0,
  zIndex: 10,
};

const backBtnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  fontSize: 20,
  cursor: 'pointer',
  color: '#6b7280',
  padding: '4px 8px',
  borderRadius: 6,
};

const titleStyle: React.CSSProperties = {
  fontSize: 18,
  fontWeight: 700,
  color: '#111827',
  margin: 0,
};

const dirtyBadge: React.CSSProperties = {
  fontSize: 11,
  padding: '2px 8px',
  background: '#fef3c7',
  color: '#92400e',
  borderRadius: 4,
  fontWeight: 500,
};

const saveBtnStyle: React.CSSProperties = {
  padding: '8px 20px',
  fontSize: 13,
  fontWeight: 600,
  border: 'none',
  borderRadius: 6,
  cursor: 'pointer',
  background: '#2563eb',
  color: '#fff',
};

const errorBanner: React.CSSProperties = {
  padding: '8px 24px',
  background: '#fef2f2',
  color: '#991b1b',
  fontSize: 13,
  borderBottom: '1px solid #fecaca',
};

const loadingStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 48,
  color: '#6b7280',
  fontSize: 14,
};

const bodyStyle: React.CSSProperties = {
  display: 'flex',
  flex: 1,
  overflow: 'hidden',
  minHeight: 0,
};

const middleColStyle: React.CSSProperties = {
  flex: 2,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  borderRight: '1px solid #e5e7eb',
};

const fieldListWrapStyle: React.CSSProperties = {
  flex: 1,
  overflow: 'auto',
  minHeight: 200,
};

const rightColStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 260,
  maxWidth: 360,
  overflow: 'auto',
};
